import { useMemo, useState } from 'react';

import {
  ContainerSingleMode,
  OuterContainerBuffon,
  FiltersContainerBuffon,
  SelectWrapper,
} from '../headerWithFilters.style';

import { StableSelect } from '../../../../../components/SharedComponents/sharedComponents.index';
import { CustomHeading, CustomText } from '../../../../../components/StyledComponents/styleComponents.index';

import { USER_ROLES } from '../../../../../components/components.constants';

import PropTypes from "prop-types";

/**
 * Strategy padrão para HeaderWithFilters
 * Mantém o comportamento atual com filtros de região e loja
 */
export const HeaderGasStation = ({ actions, ctx, uiState }) => {
  const { setCurrentStoreData, setSelectedSecondaryFilterValues, setShowInputs = () => {} } = actions || {};
  
  const { 
    selectedSecondaryFilterValues,
    allSecondaryOptions,
    allPrimaryOptions,
    labels,
    supplyDetails,
    currentStoreId,
    numberToDisplay = 0
  } = ctx || {};
  
  const { 
    isLoadingStores, 
    showInputs, 
    singleMode = false, 
    showCard = false,
    userRole,
    headerStyle = {
      useProvidedHeaderStyle: false,
      providedHeaderStyle: {}
    }
  } = uiState || {};

  const getInitialRegion = useMemo(() => {
    if (!selectedSecondaryFilterValues || !allSecondaryOptions || !allPrimaryOptions) return "0";
    
    const storeId = allSecondaryOptions.find(store => store.storeNumber === selectedSecondaryFilterValues)?.storeId;
    
    if (!storeId) return "0";
    
    for (const region of allPrimaryOptions) {
      if (region.stores?.some(s => s.storeId === storeId)) return region.regionName;
    }
    
    return "0";
  }, [selectedSecondaryFilterValues, allSecondaryOptions, allPrimaryOptions]);
  
  const [currentRegion, setCurrentRegion] = useState(getInitialRegion);
  
  const storeNumberToIdMap = useMemo(() => {
    const map = {};
    if (Array.isArray(allSecondaryOptions)) {
      allSecondaryOptions.forEach(store => {
        map[store.storeNumber] = store.storeId;
      });
    }
    return map;
  }, [allSecondaryOptions]);
  
  const storeToRegionMap = useMemo(() => {
    const map = {};
    if (Array.isArray(allPrimaryOptions)) {
      allPrimaryOptions.forEach(region => {
        if (Array.isArray(region.stores)) {
          region.stores.forEach(store => {
            map[store.storeId] = region.regionName;
          });
        }
      });
    }
    return map;
  }, [allPrimaryOptions]);
  
  const regionOptions = useMemo(() => {
    const options = [{ value: 0, label: "Todas" }];

    if (Array.isArray(allPrimaryOptions)) {
      allPrimaryOptions
        .map((region, index) => ({ region, index }))
        .sort((a, b) =>
          a.region.regionName.localeCompare(
            b.region.regionName,
            'pt-BR',
            { sensitivity: 'base' }
          )
        )
        .forEach(({ region, index }) => {
          options.push({
            value: index + 1,
            label: region.regionName
          });
        });
    }

    return options;
  }, [allPrimaryOptions]);

  const indexToRegionMap = useMemo(() => {
    const map = { 0: "ALL" };
    if (Array.isArray(allPrimaryOptions)) {
      allPrimaryOptions.forEach((region, index) => {
        map[index + 1] = region.regionName;
      });
    }
    return map;
  }, [allPrimaryOptions]);  
  
  const filteredStoreOptions = useMemo(() => {
    if (!Array.isArray(allSecondaryOptions)) return [];
    
    if (currentRegion === "0" || !currentRegion) {
      return allSecondaryOptions
        .slice()
        .sort((a, b) => a.storeNumber - b.storeNumber)
        .map(store => ({
          value: store.storeNumber,
          label: store.storeNumber
        }));
    }
    
    const selectedRegion = allPrimaryOptions?.find(region => region.regionName === currentRegion);
      
    if (!selectedRegion || !Array.isArray(selectedRegion.stores)) return [];
    
    const regionStoreIds = selectedRegion.stores.map(s => s.storeId).sort((a, b) => a - b);
    
    const filtered = allSecondaryOptions.filter(store => regionStoreIds.includes(store.storeId));
    
    return filtered
      .slice()
      .sort((a, b) => a.storeNumber - b.storeNumber)
      .map(store => ({
        value: store.storeNumber,
        label: store.storeNumber
      }));
  }, [currentRegion, allPrimaryOptions, allSecondaryOptions]);
  
  const handleStoreSelection = (selectedStoreNumber) => {
    if (showInputs) setShowInputs(false);

    const storeId = storeNumberToIdMap[selectedStoreNumber];
    setSelectedSecondaryFilterValues(storeId, selectedStoreNumber);
  };  

  const handleRegionChange = (selectedIndex) => {
    const regionName = indexToRegionMap[selectedIndex];    

    setCurrentRegion(regionName === "ALL" ? "0" : regionName);
    
    if (regionName === "ALL") {
      if (showInputs) setShowInputs(false);
      setSelectedSecondaryFilterValues(null, null);
      return;
    }
    
    if (selectedSecondaryFilterValues) {
      if (showInputs) setShowInputs(false);

      const storeId = storeNumberToIdMap[selectedSecondaryFilterValues];
      const currentStoreRegion = storeToRegionMap[storeId];
      
      if (currentStoreRegion !== regionName) setSelectedSecondaryFilterValues(null, null);
    }
  };  

  const currentRegionIndex = useMemo(() => {
    if (currentRegion === "0") return 0;
    const index = allPrimaryOptions?.findIndex(r => r.regionName === currentRegion);
    return index !== -1 ? index + 1 : 0;
  }, [currentRegion, allPrimaryOptions]);

  const defaultContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    blockSize: "100%",
    inlineSize: "100%"
  }

  // Modo single - seletor único de loja
  if (singleMode) {
    const storeOptions = supplyDetails?.map(store => ({
      value: store?.storeId,
      label: store?.storeId.toString()
    })) || [];

    const handleStoreChange = (selectedStoreId) => {
      const storeData = supplyDetails.find(s => s.storeId === selectedStoreId);
      if (storeData) {
        setCurrentStoreData(storeData.storeId);
      }
    };

    return (
      <ContainerSingleMode>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          <CustomHeading fontSize={['0.5rem', '0.8rem', '1.2rem']} margin={"0"} inlineSize={"100%"}>
            {labels.titlePage}
          </CustomHeading>

          <CustomHeading fontSize={['0.5rem', '0.6rem', '1rem']} margin={"0"} inlineSize={"100%"}>
            {labels.family}: {labels.familyName}
          </CustomHeading>  
        </div>

        <OuterContainerBuffon>
          <FiltersContainerBuffon>
            <SelectWrapper 
              style={{ 
                display: "flex", 
                flexDirection: "row", 
                alignItems: "center", 
                gap: "0.5rem", 
                gridArea: "lojas", 
                justifyContent: "end" 
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <StableSelect
                  label="Loja: "
                  options={storeOptions}
                  value={currentStoreId}
                  onChange={handleStoreChange}
                  width=""
                />
              </div>
            </SelectWrapper>
          </FiltersContainerBuffon>
        </OuterContainerBuffon>
      </ContainerSingleMode>
    );    
  }
 
  // Modo padrão - filtros de região e loja
  return (
    <div style={headerStyle.useProvidedHeaderStyle ? headerStyle.providedHeaderStyle : defaultContainerStyle}>
      <CustomHeading fontSize={['0.6rem', '1rem', '2rem']} margin={"0"} inlineSize={"100%"} fontWeight={500}>
        {labels.titlePage}
      </CustomHeading>

      {showCard && userRole && (
        <section style={{ 
          alignItems: 'center',
          display: 'flex', 
          flexDirection: 'row', 
          gap: '0.4rem', 
          justifyContent: 'space-between', 
          inlineSize: "92%" }}>
          <div
            style={{
              alignItems: "center",
              backgroundColor: "#fff", 
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              borderRadius: "0.4rem",              
              display: "flex",
              flexDirection: 'row',
              inlineSize: "100%",
              height: "auto",
              padding: "0 0.8rem 0 0.4rem"
            }}>

            <CustomHeading
              fontSize={['0.3rem', '0.6rem', '1.3rem']} textAlign={"start"} color="mediumGrey" fontWeight={500}>
              Total de Compras pendentes:
            </CustomHeading>

            <CustomText color="orange" weight="bold" fontSize={['1rem', '1.2rem', '1.8rem']}>
              {String(numberToDisplay)}
            </CustomText>
          </div>
        </section>
      )}

      {userRole !== USER_ROLES.SHOPKEEPER ? (
        <OuterContainerBuffon>
          <FiltersContainerBuffon>
            <SelectWrapper 
              style={{ 
                display: "flex", 
                flexDirection: "row", 
                alignItems: "center", 
                gap: "0.5rem", 
                gridArea: "regiao", 
                justifyContent: "end" 
              }}
            >
              <CustomText fontSize={['0.4rem', '0.6rem', '1rem']} weight="bold">
                Região
              </CustomText>
              <StableSelect
                label=""
                options={regionOptions}
                value={currentRegionIndex} 
                onChange={handleRegionChange}
                width=""
              />
            </SelectWrapper>
            
            <SelectWrapper 
              style={{ 
                display: "flex", 
                flexDirection: "row", 
                alignItems: "center", 
                gap: "0.5rem", 
                gridArea: "lojas", 
                justifyContent: "end" 
              }}
            >
              <CustomText fontSize={['0.4rem', '0.6rem', '1rem']} weight="bold">
                {labels.secondSelect}
              </CustomText>
              <div
                title={isLoadingStores ? "Carregando lojas..." : undefined}
                aria-disabled={isLoadingStores}
                style={{
                  pointerEvents: isLoadingStores ? "none" : undefined,
                  opacity: isLoadingStores ? 0.6 : 1,
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <StableSelect
                  label="Loja:"
                  options={filteredStoreOptions}
                  value={selectedSecondaryFilterValues}
                  onChange={handleStoreSelection}
                  width=""
                />
              </div>
            </SelectWrapper>
          </FiltersContainerBuffon>
        </OuterContainerBuffon>
      ) : <></>}
    </div>
  );
};

HeaderGasStation.propTypes = { 
    actions: PropTypes.shape(), 
    ctx: PropTypes.shape(), 
    uiState: PropTypes.shape() 
}